import React, { createRef } from "react";
import styled from "@emotion/styled";
import { css } from "@emotion/react";
import { useToggleState } from "@react-stately/toggle";
import { useCheckbox } from "@react-aria/checkbox";
import { AriaCheckboxProps } from "@react-types/checkbox";
import { useFocusRing } from "@react-aria/focus";
import { VisuallyHidden } from "@react-aria/visually-hidden";
import { Typography } from "../Typography";

const Label = styled.label`
  display: flex;
  padding: 5px 0;
`;

const Description = styled.div<{ isLeftOfCheckbox?: boolean }>`
  flex: 1;
  position: relative;
  top: 4px;
  ${(props) =>
    props.isLeftOfCheckbox
      ? css`
          flex: 0;
          margin-right: 10px;
        `
      : css`
          margin-left: 10px;
        `}
`;

const CheckboxSVG = styled.svg`
  height: 22px;
  width: 22px;
`;

const Box = styled.div<{
  isSelected: boolean;
  isFocused?: boolean;
  isDisabled?: boolean;
}>`
  box-sizing: border-box;
  width: 24px;
  height: 24px;
  background: ${(props) =>
    props.isDisabled
      ? props.theme.colors.mist
      : props.isSelected
      ? props.theme.colors.jungleDark
      : props.theme.colors.snowdrop};
  border-style: solid;
  border-color: ${(props) =>
    props.isDisabled
      ? props.theme.colors.mistDarker
      : props.isFocused && !props.isSelected
      ? props.theme.colors.charcoal
      : props.isFocused
      ? props.theme.colors.jungle
      : props.isSelected
      ? "transparent"
      : props.theme.colors.cloudDark};
  border-width: 1px;
  outline: none;

  &:hover {
    cursor: ${(props) => (props.isDisabled ? "default" : "pointer")};
    border-color: ${(props) =>
      props.isDisabled
        ? props.theme.colors.mistDarker
        : props.isSelected
        ? "transparent"
        : props.theme.colors.charcoal};
    background: ${(props) =>
      props.isDisabled
        ? props.theme.colors.mist
        : props.isSelected
        ? props.theme.colors.fernDark
        : props.theme.colors.snowdrop};
  }
`;

const OuterBox = styled.div<{
  isSelected: boolean;
  isFocused?: boolean;
  isDisabled?: boolean;
}>`
  width: 28px;
  height: 28px;
  background-color: ${(props) =>
    props.isFocused ? props.theme.colors.jungle : "transparent"};
  padding: 2px;
`;

const Tick = styled.path<{ isFocused?: boolean; isDisabled?: boolean }>`
  fill: ${(props) =>
    props.isDisabled
      ? props.theme.colors.cloudDark
      : props.theme.colors.snowdrop};
`;

export type CheckboxProps = AriaCheckboxProps & {
  label: string;
  className?: string;
  children?: React.ReactNode;
  helpText?: string;
};

const CheckboxInput = (props: CheckboxProps) => {
  const inputRef = createRef<HTMLInputElement>();
  const state = useToggleState(props);
  const { inputProps } = useCheckbox(props, state, inputRef);
  const { isFocusVisible, focusProps } = useFocusRing();

  return (
    <>
      <VisuallyHidden>
        <input
          id={`${props.label}_checkbox`}
          {...inputProps}
          {...focusProps}
          ref={inputRef}
          disabled={props.isDisabled}
        />
      </VisuallyHidden>
      <OuterBox
        isSelected={state.isSelected}
        isFocused={isFocusVisible}
        isDisabled={props.isDisabled}
      >
        <Box
          isSelected={state.isSelected}
          isFocused={isFocusVisible}
          isDisabled={props.isDisabled}
        >
          {state.isSelected && (
            <CheckboxSVG aria-hidden="true">
              <Tick
                isDisabled={props.isDisabled}
                transform={isFocusVisible ? "translate(3 4)" : "translate(3 4)"}
                d="M15.3443 2.67748C15.7186 2.30323 15.7186 1.69645 15.3443 1.32219C14.9701 0.947941 14.3633 0.947941 13.989 1.32219L5.5 9.81122L2.01098 6.32219C1.63672 5.94794 1.02994 5.94794 0.655689 6.32219C0.281437 6.69645 0.281437 7.30323 0.655689 7.67748L4.82236 11.8441C5.19661 12.2184 5.80339 12.2184 6.17764 11.8441L15.3443 2.67748Z"
              />
            </CheckboxSVG>
          )}
        </Box>
      </OuterBox>
    </>
  );
};

const HelpText = styled(Typography)`
  color: ${(props) => props.theme.colors.cloudDarker};
  margin-top: 5px;
`;

export const Checkbox = (props: CheckboxProps) => {
  return (
    <Label
      aria-label={props.label}
      htmlFor={`${props.label}_checkbox`}
      className={props.className}
    >
      <CheckboxInput {...props} />
      <Description>
        <Typography use="label">{props.children}</Typography>
        {props.helpText && <HelpText use="label">{props.helpText}</HelpText>}
      </Description>
    </Label>
  );
};

Checkbox.DescriptionOnLeft = (props: CheckboxProps) => {
  return (
    <Label
      aria-label={props.label}
      htmlFor={`${props.label}_checkbox`}
      className={props.className}
    >
      <Description isLeftOfCheckbox>{props.children}</Description>
      <CheckboxInput {...props} />
    </Label>
  );
};

// !important needed so overrides work for breakpoints
Checkbox.InlineLabelLink = styled(Typography)`
  font-size: inherit !important;
  line-height: inherit !important;
`;

Checkbox.WithMarginBottom = styled(Checkbox)<{
  isMarginBottomSmall?: boolean;
  $size?: number;
}>`
  margin-bottom: ${(props) =>
    !!props.$size
      ? props.$size + "px"
      : props.isMarginBottomSmall
      ? "10px"
      : "30px"};
`;
